import environmentCatalog from './video-environment-catalog.json';
import type {
  BackgroundVisionAnalysis,
  BackgroundLayoutDefinition,
  CameraCalibration,
  SpaceClass,
} from '../pages/videoBackgroundLayout';

const analysisFiles = import.meta.glob<BackgroundVisionAnalysis>('./video-background-analysis/*.json', {
  eager: true,
  import: 'default',
});

export type VideoEnvironmentId = string;

interface EnvironmentCatalogEntry {
  id: string;
  name: string;
  description: string;
  spaceClass: SpaceClass;
  imageSrc: string;
  panoramaSrc: string;
  backdrop: 'projected-plate' | 'generated-cubemap';
  cubemapFaces: readonly [string, string, string, string, string, string];
  movementMargin: number;
  camera: CameraCalibration;
  shell: {
    width: number;
    depth: number;
    height: number;
    ceiling: boolean;
    minAnchorClearance: number;
    minPairSpacing: number;
  };
  analysisFile: string;
  metadataFile?: string;
  hashes?: Record<string, unknown>;
}

export interface VideoEnvironmentDefinition extends BackgroundLayoutDefinition {
  id: VideoEnvironmentId;
  name: string;
  description: string;
  imageSrc: string;
  panoramaSrc: string;
  backdrop: 'projected-plate' | 'generated-cubemap';
  cubemapFaces?: readonly [string, string, string, string, string, string];
  movementMargin: number;
  shellDimensions: { width: number; depth: number; height: number };
  metadataFile?: string;
  hashes?: Record<string, unknown>;
}

const SPACE_CLASS_ORDER: SpaceClass[] = ['small-room', 'hallway', 'large-campus'];

function parseSpaceClass(value: string): SpaceClass {
  switch (value) {
    case 'small-room':
    case 'hallway':
    case 'large-campus':
      return value;
    default:
      throw new Error(`Invalid video environment space class '${value}'`);
  }
}

function parseBackdrop(value: string): EnvironmentCatalogEntry['backdrop'] {
  if (value === 'projected-plate' || value === 'generated-cubemap') return value;
  throw new Error(`Invalid video environment backdrop '${value}'`);
}

function parseWorldPoint(value: number[], label: string): CameraCalibration['position'] {
  if (value.length !== 3 || value.some((coordinate) => !Number.isFinite(coordinate))) {
    throw new Error(`Invalid ${label}: expected three finite coordinates`);
  }
  return [value[0], value[1], value[2]];
}

function parseCubemapFaces(value: string[], environmentId: string): EnvironmentCatalogEntry['cubemapFaces'] {
  if (value.length !== 6 || value.some((face) => typeof face !== 'string' || face.length === 0)) {
    throw new Error(`Invalid cubemap for environment '${environmentId}': expected six face paths`);
  }
  return [value[0], value[1], value[2], value[3], value[4], value[5]];
}

if (environmentCatalog.schemaVersion !== 1 || !Array.isArray(environmentCatalog.environments)) {
  throw new Error('Invalid video environment catalog: expected schemaVersion 1 and environments');
}

const catalog: { schemaVersion: number; environments: EnvironmentCatalogEntry[] } = {
  schemaVersion: environmentCatalog.schemaVersion,
  environments: environmentCatalog.environments.map((entry) => ({
    ...entry,
    spaceClass: parseSpaceClass(entry.spaceClass),
    backdrop: parseBackdrop(entry.backdrop),
    cubemapFaces: parseCubemapFaces(entry.cubemapFaces, entry.id),
    camera: {
      ...entry.camera,
      position: parseWorldPoint(entry.camera.position, `${entry.id} camera position`),
      target: parseWorldPoint(entry.camera.target, `${entry.id} camera target`),
    },
  })),
};

function loadAnalysis(entry: EnvironmentCatalogEntry): BackgroundVisionAnalysis {
  const key = entry.analysisFile.startsWith('./') ? entry.analysisFile : `./${entry.analysisFile}`;
  const analysis = analysisFiles[key];
  if (!analysis) throw new Error(`Missing analysis file '${entry.analysisFile}' for environment '${entry.id}'`);
  return analysis;
}

export const VIDEO_ENVIRONMENTS: VideoEnvironmentDefinition[] = [...catalog.environments]
  .sort((first, second) => SPACE_CLASS_ORDER.indexOf(first.spaceClass) - SPACE_CLASS_ORDER.indexOf(second.spaceClass))
  .map((entry) => {
  const analysis = loadAnalysis(entry);
  return {
    id: entry.id,
    name: entry.name,
    description: entry.description,
    imageSrc: entry.imageSrc,
    panoramaSrc: entry.panoramaSrc,
    backdrop: entry.backdrop,
    cubemapFaces: entry.cubemapFaces,
    movementMargin: entry.movementMargin,
    camera: entry.camera,
    analysis,
    shellHeight: entry.shell.height,
    ceiling: entry.shell.ceiling,
    minAnchorClearance: entry.shell.minAnchorClearance,
    minPairSpacing: entry.shell.minPairSpacing,
    shellDimensions: {
      width: entry.shell.width,
      depth: entry.shell.depth,
      height: entry.shell.height,
    },
    metadataFile: entry.metadataFile,
    hashes: entry.hashes,
  };
  });

export const SPACE_CLASS_LABEL: Record<SpaceClass, string> = {
  'small-room': 'SMALL ROOM',
  hallway: 'HALLWAY',
  'large-campus': 'LARGE CAMPUS',
};
